import type { Coding } from '@medplum/fhirtypes'

export type ScaleType = 'Qn' | 'Ord' | 'Nom' | 'Nar' | 'Ref'

const SCALE_TYPES: readonly string[] = ['Qn', 'Ord', 'Nom', 'Nar', 'Ref']

export function isScaleType(value: string): value is ScaleType {
  return SCALE_TYPES.includes(value)
}

// A quantitative code whose value is a point in time rather than a number.
export type ObservationValueKind = 'date' | 'dateTime'

export interface CodingAnswerOption {
  code: string
  display: string
  // Present when the answer came out of a value set, so the recorded value
  // names its code system. Answers read from LOINC `answer_to` carry none.
  system?: string
  // What the answer counts toward a total, from the answer set's itemWeight.
  score?: number
}

// One part of an observation recorded as components, such as the systolic
// pressure of a blood pressure.
export interface ObservationComponentSpec {
  coding: Coding
  label: string
  // Shown where space is short.
  short?: string
  // UCUM code of a `Qn` part.
  unit?: string | null
  scaleType: ScaleType
  valueKind?: ObservationValueKind
  answerOptions?: CodingAnswerOption[]
  // Without every required part there is nothing to record.
  required: boolean
}

// How a code is recorded as components. `format` places the required parts
// in the typed line, each named by its component code in braces.
export interface ObservationTemplate {
  parent: Coding
  components: ObservationComponentSpec[]
  format?: string
}

export interface CodingDetails {
  scaleType: ScaleType
  unit: string | null
  answerOptions: CodingAnswerOption[]
  display: string
  // Set, with no `answerOptions`, when the code's answer list is too long to
  // carry on the node. Answers are then searched in this value set.
  answerValueSet?: string
  // Set on a `Ref` code: the resource type the recorded answer points at.
  referenceResourceType?: string
  // Set on a `Qn` code answered with a date or timestamp, which then has no
  // unit.
  valueKind?: ObservationValueKind
  // Set on a code recorded as components: its parts, and how the required
  // ones read when typed.
  components?: ObservationComponentSpec[]
  format?: string
  // A LOINC panel. Without a template it is recorded as one number.
  isPanel?: boolean
  // A panel's LOINC CLASS and CLASSTYPE, which say what kind of records it
  // makes (vital signs, laboratory, survey).
  loincClass?: string
  loincClassType?: string
  /* A choice that records every selected answer, each as its own
   * Observation. Absent on a question that takes one answer. */
  repeats?: boolean
}

const SIDE_BY_SIDE_ANSWERS = 4
const SIDE_BY_SIDE_LABEL_LENGTH = 24

// Whether a list of answers is short enough to offer side by side as
// buttons, rather than in a list that has to be opened first.
export function answersFitSideBySide(labels: string[]): boolean {
  return (
    labels.length <= SIDE_BY_SIDE_ANSWERS &&
    labels.every((label) => label.length <= SIDE_BY_SIDE_LABEL_LENGTH)
  )
}
